/**
 * Geteiltes visuelles Rezept fuer Eingabe-Matrizen (Tabellen mit Beschaeftigtengruppen-
 * bzw. Kategorie-Spalten und Zahlenfeld-Zellen). Bewusst NUR das Styling-Objekt, KEIN
 * generischer <MatrixTable>-Komponentenbaustein (D-03) — jede Matrix-Section behaelt ihre
 * eigene Tabellenstruktur (Spalten, Kopfzeilen, Summenzeilen) und importiert hier nur die
 * gemeinsame Optik.
 */

/** Tabellen-Styles fuer Matrix-Formulare (graue Kopfzeile, vertikal zentrierte Zellen). */
export const MATRIX_TABLE_STYLES = {
  th: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--mantine-color-vdzGray-6)',
    whiteSpace: 'normal' as const,
    verticalAlign: 'bottom' as const,
    paddingBottom: 12,
  },
  td: { verticalAlign: 'middle' as const },
};

/**
 * Spaltenbreiten/-ausrichtung der Kopfzellen. Ohne diese Mindestbreiten springen die
 * Spalten je nach Inhalt in der Breite; die Werte sind als `style`-Prop an <Table.Th>
 * gedacht und ergaenzen MATRIX_TABLE_STYLES.th (keine Ueberschneidung der Keys).
 */

/** Erste Spalte: Kennzahl-/Zeilenbezeichnung, linksbuendig. */
export const MATRIX_LABEL_TH = { minWidth: 260, textAlign: 'left' as const };

/** Datenspalten mit Eingabefeldern, Kopf zentriert ueber dem Feld. */
export const MATRIX_VALUE_TH = { minWidth: 150, textAlign: 'center' as const };

/** Abschliessende Summenspalte (nur Text, kein Eingabefeld). */
export const MATRIX_TOTAL_TH = { minWidth: 120, textAlign: 'right' as const };

/**
 * Kompaktere Datenspalten-Breite fuer die 9-spaltige Formular-D-Matrix (Claude's Discretion,
 * Plan 23-02). `MATRIX_VALUE_TH` bleibt bei `minWidth: 150` fuer die 3-spaltige Unfallmatrix
 * unangetastet -- eine zweite Konstante additiv daneben statt einer Aenderung an der ersten,
 * damit bestehende Matrizen (Unfall) sich nicht schmaeler machen, sobald eine 9-spaltige Matrix
 * (Formular D) dieselbe Datei importiert.
 */
export const MATRIX_VALUE_TH_COMPACT = { minWidth: 90, textAlign: 'center' as const };

/**
 * Schluesselspalte der beiden Brennstoff-Tabellen mit AVV-Auswahl (D-12/D-14, Phase 26).
 * Additiv neben MATRIX_LABEL_TH -- die Primaerbrennstoff-Tabellen (D-13) haben keine
 * Schluesselspalte und importieren diese Konstante nicht.
 *
 * `maxWidth` gehoert zwingend dazu: die Tabellen fahren `table-layout: auto`, dort ist die
 * Spaltenbreite die max-content-Breite ihres Inhalts. Ein reines `minWidth` laesst die Spalte
 * deshalb mit dem Verordnungstitel mitwachsen (gemessen 415px bei „19 12 07", 501px beim
 * laengsten Titel) und schiebt die Tabelle in den Waagerecht-Scroll -- genau das, was D-14 mit
 * den schmaleren Zahlenspalten vermeiden wollte. Erst die Deckelung laesst das `truncate` des
 * Titels im geschlossenen Feld greifen (D-11).
 */
export const MATRIX_WASTE_CODE_TH = {
  minWidth: 280,
  width: 280,
  maxWidth: 280,
  textAlign: 'left' as const,
};

/**
 * Schmalere Zahlenspalten NEBEN MATRIX_VALUE_TH, nicht als dessen Aenderung (D-14-Begruendung
 * identisch zu MATRIX_VALUE_TH_COMPACT): nur die beiden Tabellen mit Schluesselspalte
 * (Sekundaer-, Sonstige Brennstoffe) nutzen sie, die vier Primaertabellen bleiben bei 150px.
 */
export const MATRIX_VALUE_TH_NARROW = { minWidth: 120, textAlign: 'center' as const };

/**
 * Fixierte erste Spalte fuer Matrizen, die WAAGERECHT scrollen (D-09).
 *
 * Frueher stand hier zusaetzlich ein Rezept fuer eine mitlaufende Kopfzeile (`top: 0`). Das
 * setzte voraus, dass die Tabelle in einem eigenen, hoehenbegrenzten Scroll-Container liegt --
 * und genau diese Begrenzung erzeugte in Formular D einen zweiten, leicht zu uebersehenden
 * Rollbalken mitten im Formular: wer ihn nicht bemerkte, hielt die Zementarten darunter fuer
 * nicht vorhanden. Die Hoehenbegrenzung ist deshalb entfallen, und mit ihr die Grundlage fuer
 * eine vertikal fixierte Kopfzeile: `position: sticky` bezieht sich immer auf den naechsten
 * scrollenden Vorfahren, und das ist wegen `overflow-x` weiterhin der Tabellenrahmen, nicht die
 * Seite. Eine am Fensterrand klebende Kopfzeile ist so nicht erreichbar, ohne das waagerechte
 * Scrollen aufzugeben.
 *
 * Die Eckzelle braucht den hoeheren z-index, damit sie beim waagerechten Scrollen ueber den
 * Zeilenzellen liegt.
 */
export const MATRIX_STICKY_LABEL_TH = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
};

/** Erste Spalte im Tbody: waagerecht (left) fixiert. */
export const MATRIX_STICKY_LABEL_TD = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
};

/**
 * Typ-Spalte mit einem `Select` (Primaer/Sekundaer), Einsatzstoffe-Tabellen (Phase 31,
 * T-31-13-Kehraus-Befund). Dieselbe `table-layout: auto`-Lektion wie bei
 * `MATRIX_WASTE_CODE_TH`, nur in die andere Richtung: dort wuchs die Spalte ueber die
 * Combobox-Textbreite hinaus, hier blieb sie bei einer reinen `w={120}`-Angabe zu schmal fuer
 * den `Select` -- Platzhalter "Typ waehlen" (69px) und "Sekundaer" (60px) wurden vom
 * Clear-/Chevron-Sektionsraum (34px) beschnitten ("Sekur ✕" statt "Sekundaer", im Browser
 * gemessen). 150px (statt vorher 120px) belaesst nach Zellenpolsterung und Sektionsraum rund
 * 80px Textflaeche -- ausreichend fuer den laengsten Optionstext samt Platzhalter.
 */
export const MATRIX_TYPE_SELECT_TH = {
  minWidth: 150,
  width: 150,
  maxWidth: 150,
  textAlign: 'left' as const,
};
